import { z } from "zod";
import { IsoDateTime } from "../common.ts";

// Organization identity provider routes of the Explorer API (`iam`, design D4 and D10): managed
// only by a console user with `users.manage` on every operator of the caller's organization. The
// client secret is write-only: every response carries it masked.

/** What every response shows instead of the client secret. */
export const MASKED_CLIENT_SECRET = "********";

export const IdentityProviderKindV1 = z
  .enum(["saml", "oidc"])
  .meta({ id: "IdentityProviderKindV1" });
export const IdentityProviderStatusV1 = z
  .enum(["draft", "active", "inactive"])
  .meta({ id: "IdentityProviderStatusV1" });

const HttpsUrl = z.url({ protocol: /^https$/ }).max(2048);

export const IdentityProviderViewV1 = z
  .object({
    identity_provider_id: z.uuid(),
    kind: IdentityProviderKindV1,
    /** SAML metadata URL or OIDC issuer. */
    issuer_or_metadata_url: HttpsUrl,
    client_id: z.string().min(1).max(256).nullable(),
    /** Always masked (`********`) for OIDC, `null` for SAML: never the secret itself. */
    client_secret: z.literal(MASKED_CLIENT_SECRET).nullable(),
    status: IdentityProviderStatusV1,
    last_tested_at: IsoDateTime.nullable(),
    last_test_succeeded: z.boolean().nullable(),
    updated_by: z.uuid(),
    updated_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "IdentityProviderViewV1" });
export type IdentityProviderViewV1 = z.infer<typeof IdentityProviderViewV1>;

/**
 * What the organization's Administrator registers in their own IdP (derived from the console user
 * pool's hosted domain; non-secret). `null` in the response when SSO is not served by the user pool
 * (local mock or disabled).
 */
export const IdentityProviderRegistrationValuesV1 = z
  .object({
    /** SAML: assertion consumer service (reply) URL. */
    saml_acs_url: HttpsUrl,
    /** SAML: service provider entity id (audience). */
    saml_entity_id: z.string().min(1).max(256),
    /** OIDC: redirect (callback) URI. */
    oidc_redirect_uri: HttpsUrl,
  })
  .strict()
  .meta({ id: "IdentityProviderRegistrationValuesV1" });
export type IdentityProviderRegistrationValuesV1 = z.infer<
  typeof IdentityProviderRegistrationValuesV1
>;

/** `GET /organization/identity-provider`: read-only (`can_manage` false) for a partial Administrator. */
export const IdentityProviderSettingsResponseV1 = z
  .object({
    can_manage: z.boolean(),
    identity_provider: IdentityProviderViewV1.nullable(),
    registration_values: IdentityProviderRegistrationValuesV1.nullable(),
  })
  .strict()
  .meta({ id: "IdentityProviderSettingsResponseV1" });
export type IdentityProviderSettingsResponseV1 = z.infer<typeof IdentityProviderSettingsResponseV1>;

/**
 * `PUT /organization/identity-provider`: registers the organization's provider (`201`, status
 * `draft`) or updates it (`200`; a new test is then required). `client_secret` is required to
 * register an OIDC provider and optional on update (omitted = keep the current one).
 */
export const PutIdentityProviderRequestV1 = z
  .object({
    kind: IdentityProviderKindV1,
    issuer_or_metadata_url: HttpsUrl,
    client_id: z.string().trim().min(1).max(256).optional(),
    client_secret: z.string().min(1).max(1024).optional(),
  })
  .strict()
  .meta({ id: "PutIdentityProviderRequestV1" });
export type PutIdentityProviderRequestV1 = z.infer<typeof PutIdentityProviderRequestV1>;

/** `POST /organization/identity-provider/test`: a successful test is required to activate. */
export const IdentityProviderTestResponseV1 = z
  .object({ succeeded: z.boolean() })
  .strict()
  .meta({ id: "IdentityProviderTestResponseV1" });
export type IdentityProviderTestResponseV1 = z.infer<typeof IdentityProviderTestResponseV1>;
